import React, { useState, useEffect } from 'react';
import { ChevronDown, Loader2, Image } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { format } from 'date-fns';
import { Task, WorkOrder, BidPhoto } from '../../types/workOrders';
import { getBidPhotos } from '../../utils/api';
import { getWorkOrderDetails, saveWorkOrderDetails, addPendingAction } from '../../utils/offlineDb';
import { toast } from 'react-hot-toast';
import 'react-photo-view/dist/react-photo-view.css';

interface TaskPhotoGalleryProps {
  task: Task;
  workOrder: WorkOrder;
  onClose: () => void;
}

export function TaskPhotoGallery({ task, workOrder, onClose }: TaskPhotoGalleryProps) {
  const [photos, setPhotos] = useState<BidPhoto[]>([]);
  const [loading, setLoading] = useState(true);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const loadPhotos = async () => {
      try {
        const cachedDetails = await getWorkOrderDetails(workOrder.report_id);
        if (cachedDetails?.bidPhotos) {
          const taskPhotos = cachedDetails.bidPhotos.filter(photo => photo.taskItem === task.desc);
          setPhotos(taskPhotos);
          setLoading(false);
        }

        if (!navigator.onLine) {
          if (!cachedDetails?.bidPhotos) {
            setPhotos([]);
          }
          setLoading(false);
          return;
        }

        // If online, fetch fresh photos
        try {
          const allPhotos = await getBidPhotos(workOrder.report_id);
          const taskPhotos = allPhotos.filter(photo => photo.taskItem === task.desc);
          setPhotos(taskPhotos);
          
          // Cache the photos
          await saveWorkOrderDetails(
            workOrder.report_id,
            cachedDetails?.jobNotes || [],
            allPhotos
          );
        } catch (error) {
          console.error('Error fetching photos:', error);
          if (!cachedDetails?.bidPhotos) {
            setPhotos([]);
          }
        }
      } catch (error) {
        console.error('Error loading photos:', error);
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
  }, [workOrder.report_id, task.desc]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-light-card dark:bg-dark-card rounded-none sm:rounded-lg p-4 sm:p-6 w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Photos for: {task.desc}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Image className="h-12 w-12 mx-auto mb-4" />
            <p>No photos available for this task</p>
          </div>
        ) : (
          <PhotoProvider
            photoClosable={false}
            maskOpacity={0.8}
            maskClassName="backdrop-blur-sm"
            loadingElement={<Loader2 className="h-8 w-8 animate-spin" />}
            toolbarRender={({ onScale, scale }) => {
              return ( 
                <div
                  className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onScale(scale + 0.5);
                    }}
                    className="text-white hover:text-gray-200 px-2"
                  >
                    +
                  </button>
                  <span className="text-white">{Math.round(scale * 100)}%</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onScale(scale - 0.5);
                    }}
                    className="text-white hover:text-gray-200 px-2"
                  >
                    -
                  </button>
                </div>
              );
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="bg-gray-50 dark:bg-dark-card rounded-lg overflow-hidden border border-light-border dark:border-dark-border"
                >
                  <PhotoView 
                    src={photo.url}
                    overlay={
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-3 text-sm backdrop-blur-sm space-y-1">
                        <p>{format(new Date(photo.createdAt), 'MMM d, yyyy HH:mm:ss')}</p>
                        {photo.metadata?.latitude && photo.metadata?.longitude && (
                          <p className="text-gray-300">
                            GPS: {photo.metadata.latitude.toFixed(6)}, {photo.metadata.longitude.toFixed(6)}
                          </p>
                        )}
                      </div>
                    }
                  >
                    <img
                      src={photo.url}
                      alt={photo.taskItem}
                      className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                    />
                  </PhotoView>
                  <div className="p-2">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {format(new Date(photo.createdAt), 'MMM d, yyyy HH:mm:ss')}
                      </p>
                      {photo.metadata?.latitude && photo.metadata?.longitude && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          GPS: {photo.metadata.latitude.toFixed(6)}, {photo.metadata.longitude.toFixed(6)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </PhotoProvider>
        )}
      </div>
    </div>
  );
}